import React, { createContext, useCallback, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getMessageAnalysis, saveMessageAnalysis } from "../../api/messages";
import useOfficeContext from "../../hooks/useOfficeContext";
import { Attachment, AttachmentComparison, SavedAttachment, SavedComparisonAttachment } from "../../types/messages";
import {
  isSavedAttachmentType,
  isAttachmentType,
  isAttachmentComparisonType,
  isSavedComparisonType,
} from "../../utils";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import useAuthContext from "../../hooks/useAuthContext";


export const AppContext = createContext<AppContextType>(null);

export type AppContextType = {
  attachments: Array<Attachment>;
  savedAttachments: Array<SavedAttachment>;
  attachmentsComparison: Array<any>;
  savedAttachmentsComparison: Array<SavedComparisonAttachment>;
  workspaceName: string;
  initialLoading: boolean;
  isRefetching: boolean;
  isFollowed: boolean;
  refetch: () => void;
  onSavePress: () => void;
  isSaving: boolean;
  checkboxValues: CheckboxAnalysis;
  isChecked: (value: CheckboxValue) => boolean;
  addCheck: (value: CheckboxValue) => void;
  unCheck: (value: CheckboxValue) => void;
  getAttachment: (id: string) => Attachment;
  getCheckboxLabel: (type: SaveCheckboxType) => string;
};

type Props = {
  children: React.ReactNode;
};

export type CheckboxValue = {
  id: string;
  type: SaveCheckboxType;
};

export type SaveCheckboxType = "compare" | "attach" | "new" | "compareNewDocument";

export type CheckboxAnalysis = Array<CheckboxValue>;

const AppContainer: React.FC<Props> = (props) => {
  const { children } = props;

  const appInsights = useAppInsightsContext();
  const { itemIds } = useOfficeContext();
  const { tokenData } = useAuthContext();
  const msUserId = tokenData.oid;

  const [checkboxValues, setCheckboxValues] = useState<CheckboxAnalysis>([]);

  // v5: Removed second parameter (query function), now passed as object property
  // v5: Removed third parameter (options), all options now in single object
  const { data, status, refetch, isRefetching, isPending } = useQuery({
    queryKey: ["messageAnalysis", itemIds[0]],
    queryFn: () => {
      return getMessageAnalysis(itemIds[0],msUserId);
    },
    enabled: itemIds.length === 1,
  });

  // v5: mutationFn replaces first parameter, options now in single object
  // v5: onSuccess still works but consider using useEffect for side effects
  const { status: mutationStatus, mutate, isPending: isMutationPending } = useMutation({
    mutationFn: async (values: CheckboxAnalysis) => {
      await saveMessageAnalysis(values);
      return null;
    },
    onSuccess: () => {
      setCheckboxValues([]);
      refetch();
    },
  });

  const {
    savedAttachments,
    attachments,
    attachmentsComparison,
    savedAttachmentsComparison,
  }: {
    savedAttachments: Array<SavedAttachment>;
    attachments: Array<Attachment>;
    attachmentsComparison: Array<AttachmentComparison>;
    savedAttachmentsComparison: Array<SavedComparisonAttachment>;
  } = useMemo(() => {
    return {
      attachmentsComparison: data && data.attachments ? data.attachments.filter(isAttachmentComparisonType) : [],
      attachments: data && data.attachments ? data.attachments.filter(isAttachmentType) : [],
      savedAttachments: data && data.attachments ? data.attachments.filter(isSavedAttachmentType) : [],
      savedAttachmentsComparison: data && data.attachments ? data.attachments.filter(isSavedComparisonType) : [],
    };
  }, [data]);

  const getCheckboxLabel = (type: SaveCheckboxType): string => {
    switch (type) {
      case "attach":
        return "Attachment as new version";
      case "compare":
        return "Comparison as new version";
      case "compareNewDocument":
        return "Comparison as new document";
      case "new":
        return "Attachment as new document";
    }
  };

  const onSavePress = async () => {
    checkboxValues.forEach((checkbox) => {
      appInsights.trackEvent({ name: `Save attachment type: ${getCheckboxLabel(checkbox.type)}` });
    });

    mutate(checkboxValues);
  };

  const isChecked = (value: CheckboxValue): boolean => {
    return checkboxValues.some((item) => {
      return item.id === value.id && item.type === value.type;
    });
  };

  const addCheck = (value: CheckboxValue) => {
    setCheckboxValues((state) => {
      return state.concat([value]);
    });
  };

  const unCheck = (value: CheckboxValue) => {
    setCheckboxValues((state) => {
      return state.filter((item) => {
        return !(item.id === value.id && item.type === value.type);
      });
    });
  };

  const getAttachment = useCallback(
    (id: string): Attachment => {
      return data?.attachments.find((attachment) => {
        return attachment.attachmentId === id;
      }) as Attachment;
    },
    [data]
  );

  return (
    <AppContext.Provider
      value={{
        isRefetching,
        refetch: () => {
          refetch();
        },
        // v5: Use isPending instead of status === "loading"
        initialLoading: isPending,
        attachments,
        savedAttachments,
        attachmentsComparison,
        savedAttachmentsComparison,
        onSavePress,
        isChecked,
        addCheck,
        unCheck,
        getCheckboxLabel,
        getAttachment,
        checkboxValues,
        isFollowed: status === "success" ? data.isFollowed : false, 
        // v5: Use isPending for mutation loading state
        isSaving: isMutationPending,
        workspaceName: status === "success" ? data.workspaceName : null,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContainer;